import React from "react";
import classnames from "classnames";
import diffDays from "date-fns/differenceInDays";
import diffText from "date-fns/formatDistanceStrict";
import parse from "date-fns/parseISO";
import startOfDay from "date-fns/startOfDay";
import { ModalHeader, ModalBody } from "reactstrap";
import { Clock, Link } from "react-feather";

import { longDate } from "../../../common/helpers/date";

import { MarketingActivity as Activity, MarketingObjective as Objective } from "../../../api";

interface ObjectiveModalContentProps {
  objective: Objective;
  activitiesById: Record<string, Activity | undefined>;
  showLinks: boolean;
  handleSelectActivity: (activityId: string) => any;
}

const ObjectiveModalContent: React.FC<ObjectiveModalContentProps> = ({
  objective,
  activitiesById,
  showLinks,
  handleSelectActivity,
}) => {

  const today = startOfDay(new Date());
  const deadlineDate = objective.dueDate ? parse(objective.dueDate) : today;
  const deadlineDays = diffDays(deadlineDate, today);
  const isOverdue = deadlineDays < 0;
  const deadlineText = isOverdue
    ? `${diffText(today, deadlineDate)} overdue`
    : `${diffText(deadlineDate, today)} left`;

  return (
    <>
      <ModalHeader>
        <span className="text-primary">{objective.name}</span>
      </ModalHeader>

      {!!objective.dueDate && objective.isActive && (
        <ModalBody className={classnames({
          "d-flex align-items-center py-2": true,
          "bg-danger text-light": deadlineDays < 0,
          "bg-neutral text-light": 0 < deadlineDays,
        })}>
          <Clock size={15} />
          <span className="ml-2">{deadlineText}</span>
        </ModalBody>)}

      {/* Description */}
      {!!objective.description && (
        <ModalBody>
          <div>
            {objective.description}
          </div>
        </ModalBody>
      )}

      <ModalBody>
        {/* Status */}
        <div className="d-flex justify-content-between">
          <strong>Status</strong>
          <div>{objective.status}</div>
        </div>
        {/* Deadline */}
        <div className="mt-2 d-flex justify-content-between">
          <strong>Deadline</strong>
          <div>
            <div>
              {!!objective.dueDate
                ? longDate(deadlineDate)
                : <>-</>}
            </div>
          </div>
        </div>
        {/* Progress */}
        <div className="mt-2 d-flex justify-content-between">
          <strong>Progress</strong>
          <div>
            {typeof objective.progress === "number"
              ? (<>{objective.progress}%</>)
              : (<>-</>)}
          </div>
        </div>
      </ModalBody>

      {/* Activities */}
      {showLinks && !!objective.activityIds?.length && (
        <ModalBody className="pt-0">
          <div className="mb-2">
            <strong>Activities</strong>
          </div>
          <ul className="m-0 p-0">
            {objective.activityIds
              .filter(activityId => activityId in activitiesById)
              .map(activityId => (
                <li key={activityId} className="mt-1 d-flex">
                  <Link size={15} />
                  <span className="ml-1">
                    <a href={`#${activityId}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelectActivity(activityId);
                      }}>{activitiesById[activityId]?.name}</a>
                  </span>
                </li>
              ))}
          </ul>
        </ModalBody>
      )}
    </>
  )
};

export default ObjectiveModalContent;
